import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import axiosInstance from "../../services/auth";
import { useCombinedContext } from "../../context/CombinedContext";

const Budget = ({ vessel, onClose, fetchVessel }) => {
  const { formatPrice } = useCombinedContext();
  // Define array of month names for the dropdown.
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Generate an array of years from (current year - 5) to (current year + 5).
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 5; i <= currentYear + 5; i++) {
    years.push(i);
  }

  // Local state for form inputs.
  const [month, setMonth] = useState("");
  const [year, setYear] = useState(currentYear.toString());
  const [amount, setAmount] = useState("");

  // State for editing mode. When not null, we're editing an existing record.
  const [editingBudgetId, setEditingBudgetId] = useState(null);

  // Local state for the budget list so that UI updates immediately.
  const [budgetData, setBudgetData] = useState(vessel?.budget || []);

  // State for delete confirmation modal.
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [budgetToDelete, setBudgetToDelete] = useState(null);

  // Update local budgetData whenever vessel prop changes.
  useEffect(() => {
    setBudgetData(vessel?.budget || []);
  }, [vessel]);

  // Helper: compute total amount.
  const totalAmount = budgetData.reduce(
    (acc, item) => acc + Number(item.amount || 0),
    0
  );

  const handleOutsideClick = (e) => {
    if (e.target.classList.contains("modal")) {
      onClose();
    }
  };

  // Open delete confirmation modal.
  const openDeleteModal = (budgetid) => {
    setBudgetToDelete(budgetid);
    setShowDeleteModal(true);
  };

  // Close delete confirmation modal.
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setBudgetToDelete(null);
  };

  // Confirm deletion from the modal.
  const confirmDelete = async () => {
    try {
      await axiosInstance.delete(
        `/vessel/budget/${vessel._id}/${budgetToDelete}`
      );
      toast.success("Budget deleted!");
      setBudgetData((prev) =>
        prev.filter((item) => item._id !== budgetToDelete)
      );

      // If the deleted entry is currently being edited, reset form.
      if (editingBudgetId === budgetToDelete) {
        setMonth("");
        setYear(currentYear.toString());
        setAmount("");
        setEditingBudgetId(null);
      }
    } catch (error) {
      // console.error("Delete error:", error);
      toast.error(error.response?.data?.message || "Error deleting budget");
    }
    closeDeleteModal();
  };

  // Add a new budget entry.
  const handleAddBudget = async () => {
    // Validate inputs.
    if (!month || !year || !amount || isNaN(parseFloat(amount))) {
      toast.error("Please fill in valid month, year and amount.");
      return;
    }

    // Check for duplicates: if an entry with same month and year already exists.
    if (budgetData.some((item) => item.month === month && item.year === year)) {
      toast.warning("Budget for this month and year is already added.");
      return;
    }

    try {
      await axiosInstance.post(`/vessel/budget/${vessel._id}`, {
        month,
        year,
        amount: parseFloat(amount),
      });
      toast.success("Budget added!");

      // Create a new budget object with a temporary _id.
      const newBudget = {
        _id: Date.now().toString(),
        month,
        year,
        amount: parseFloat(amount),
      };
      setBudgetData((prev) => [...prev, newBudget]);

      // Reset form fields.
      setMonth("");
      setYear(currentYear.toString());
      setAmount("");
    } catch (error) {
      // console.error("Add error:", error);
      toast.error(error.response?.data?.message || "Error adding budget");
    }
  };

  // Update an existing budget entry.
  const handleUpdateBudget = async () => {
    // Validate inputs.
    if (!month || !year || !amount || isNaN(parseFloat(amount))) {
      toast.error("Please fill in valid month, year and amount.");
      return;
    }

    try {
      await axiosInstance.patch(
        `/vessel/budget/${vessel._id}/${editingBudgetId}`,
        {
          month,
          year,
          amount: parseFloat(amount),
        }
      );
      toast.success("Budget updated!");

      setBudgetData((prev) =>
        prev.map((item) =>
          item._id === editingBudgetId
            ? { ...item, month, year, amount: parseFloat(amount) }
            : item
        )
      );

      // Reset form fields and editing mode.
      setMonth("");
      setYear(currentYear.toString());
      setAmount("");
      setEditingBudgetId(null);
    } catch (error) {
      // console.error("Update error:", error);
      toast.error(error.response?.data?.message || "Error updating budget");
    }
  };

  // When editing, prefill form fields and set editingBudgetId.
  const handleEditPrefill = (budgetItem) => {
    setMonth(budgetItem.month);
    setYear(budgetItem.year);
    setAmount(budgetItem.amount);
    setEditingBudgetId(budgetItem._id);
  };

  return (
    <div>
      {/* Main Modal */}
      <div
        className="modal show d-block"
        tabIndex="-1"
        onClick={handleOutsideClick}
        style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
      >
        <ToastContainer />
        <div
          className="modal-dialog modal-lg modal-dialog-centered"
          role="document"
        >
          <div className="modal-content">
            <div className="modal-header text-white text-center vesel_form">
              <h5 className="modal-title">Manage Budget</h5>
              <button
                type="button"
                className="btn-close text-white"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body vesel_form">
              {/* Table listing existing budget entries */}
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Year</th>
                    <th>Amount $</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {budgetData?.length > 0 ? (
                    budgetData.map((item) => (
                      <tr key={item._id}>
                        <td>{item.month}</td>
                        <td>{item.year}</td>
                        <td>$ {formatPrice(item.amount)}</td>
                        <td>
                          <button
                            className="btn btn-outline-success btn-sm me-2"
                            onClick={() => handleEditPrefill(item)}
                          >
                            <i className="bi bi-pencil-square"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => openDeleteModal(item._id)}
                          >
                            <i className="bi bi-trash3"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">
                        No budget entries found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Display total amount */}
              <div className="mb-3 h6">
                <strong>Total Amount:</strong> ${formatPrice(totalAmount)}
              </div>

              {/* Form for adding/updating a budget entry */}
              <div className="my-3">
                <h6>{editingBudgetId ? "Update Budget" : "Add New Budget"}</h6>
                <div className="d-flex gap-2 align-items-center">
                  {/* Month Dropdown */}
                  <select
                    className="form-select"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                  >
                    <option value="">Select Month</option>
                    {months.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>

                  {/* Year Dropdown */}
                  <select
                    className="form-select"
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                  >
                    {years.map((yr) => (
                      <option key={yr} value={yr}>
                        {yr}
                      </option>
                    ))}
                  </select>

                  <input
                    type="number"
                    className="form-control"
                    placeholder="Amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />

                  {editingBudgetId ? (
                    <button
                      className="btn btn-outline-warning"
                      onClick={handleUpdateBudget}
                    >
                      Update
                    </button>
                  ) : (
                    <button
                      className="btn btn-outline-success"
                      onClick={handleAddBudget}
                    >
                      Add
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeDeleteModal}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this budget entry?</p>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={closeDeleteModal}
                >
                  Cancel
                </button>
                <button className="btn btn-danger" onClick={confirmDelete}>
                  Confirm Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Budget;
